/**
 * Display rules of the connection contract. Pure and tenant-agnostic.
 *
 * The two `null` toman cases are different displays (handoff «Balance»):
 *   - `suppressedBelowOneToman: true`  → «کمتر از ۱ تومان», never 0;
 *   - no stored conversion              → «مبلغ تومان هنوز تعیین نشده».
 * Amounts stay strings; they are grouped by string slicing, never `Number()`.
 */
import type {
  AiExecution,
  AiExecutionStatus,
  AiMoney,
  AiSettlement,
  AiSettlementStatus,
  AiUsageTokens,
} from './contract.ts';

export const LABEL_BELOW_ONE_TOMAN = 'کمتر از ۱ تومان';
export const LABEL_TOMAN_UNSET = 'مبلغ تومان هنوز تعیین نشده';

const PERSIAN_DIGITS = '۰۱۲۳۴۵۶۷۸۹';

function toPersianDigits(text: string): string {
  return text.replace(/\d/g, (digit) => PERSIAN_DIGITS[Number(digit)] ?? digit);
}

/** Groups an integer string with «٬» without converting it to a number. */
export function groupDigits(integer: string): string {
  if (!/^-?\d+$/.test(integer)) return integer;
  const negative = integer.startsWith('-');
  const digits = negative ? integer.slice(1) : integer;
  const grouped = digits.replace(/\B(?=(\d{3})+(?!\d))/g, '٬');
  return toPersianDigits(`${negative ? '-' : ''}${grouped}`);
}

/** «۲۵۰٬۰۰۰ تومان», «کمتر از ۱ تومان» or «مبلغ تومان هنوز تعیین نشده». */
export function formatMoney(money: AiMoney | null): string {
  if (money === null) return '—';
  if (money.toman !== null) return `${groupDigits(money.toman)} تومان`;
  return money.suppressedBelowOneToman ? LABEL_BELOW_ONE_TOMAN : LABEL_TOMAN_UNSET;
}

/** Formats an AiMoney balance in raw tokens (at 5 tokens per Toman): «۱٬۲۵۰٬۰۰۰ توکن». */
export function formatMoneyAsTokens(money: AiMoney | null): string {
  if (money === null) return '—';
  if (money.toman !== null) {
    const tokens = BigInt(money.toman) * 5n;
    return `${groupDigits(tokens.toString())} توکن`;
  }
  return money.suppressedBelowOneToman ? 'کمتر از ۵ توکن' : 'نامشخص';
}

/** Same rules for a bare `amountToman` string (`null` = pending / not determined). */
export function formatAmountToman(amountToman: string | null): string {
  return amountToman === null ? '—' : `${groupDigits(amountToman)} تومان`;
}

export const LABEL_TOKENS_UNKNOWN = 'نامشخص';

/** A token counter string, grouped; `null` is «نامشخص», never 0. */
export function formatTokens(tokens: string | null): string {
  return tokens === null ? LABEL_TOKENS_UNKNOWN : groupDigits(tokens);
}

/**
 * Real total tokens consumed: input + output counted in a single unit.
 * Cached input and reasoning are already accounted for in input/output, so they
 * are not added again to prevent double counting.
 * When usage is unknown (null or both null), returns null (distinct from zero).
 */
export function totalUsageTokens(usage: AiUsageTokens | null | undefined): string | null {
  if (!usage) return null;
  if (usage.inputTokens === null && usage.outputTokens === null) return null;
  const inTok = usage.inputTokens ? BigInt(usage.inputTokens) : 0n;
  const outTok = usage.outputTokens ? BigInt(usage.outputTokens) : 0n;
  return (inTok + outTok).toString();
}

export interface UsageBreakdownRow {
  readonly key: 'input' | 'cachedInput' | 'output' | 'reasoning';
  readonly label: string;
  readonly value: string;
}

/**
 * Usage-detail rows: input, cached input, output and reasoning tokens kept
 * apart. They are never added together and never turned into a money figure
 * or a "remaining tokens" balance - models and input/output carry different
 * rates, so a token total says nothing about cost. `null` usage (pending,
 * failed, no data) yields no rows rather than zeros.
 */
export function usageBreakdown(
  usage: AiUsageTokens | null | undefined,
): readonly UsageBreakdownRow[] {
  if (!usage) return [];
  return [
    { key: 'input', label: 'توکن ورودی', value: formatTokens(usage.inputTokens) },
    {
      key: 'cachedInput',
      label: 'توکن ورودی کش‌شده',
      value: formatTokens(usage.cachedInputTokens),
    },
    { key: 'output', label: 'توکن خروجی', value: formatTokens(usage.outputTokens) },
    { key: 'reasoning', label: 'توکن استدلال', value: formatTokens(usage.reasoningTokens) },
  ];
}

/** Quote ceilings carry no conversion info, so a missing figure is «نامشخص». */
export const LABEL_QUOTE_UNAVAILABLE = 'نامشخص';

export function formatQuoteCeiling(amountToman: string | null): string {
  if (amountToman === null) return LABEL_QUOTE_UNAVAILABLE;
  const tokens = BigInt(amountToman) * 5n;
  return `${groupDigits(tokens.toString())} توکن`;
}

const EXECUTION_LABELS: Readonly<Record<AiExecutionStatus, string>> = {
  not_started: 'شروع نشده',
  reserved: 'رزرو شده، اجرا شروع نشده',
  running: 'در حال اجرا',
  completed: 'اجرا انجام شد',
  failed: 'اجرا ناموفق بود',
  cancelled: 'اجرا لغو شد',
  reconciliation: 'نتیجه در حال تطبیق است',
};

const SETTLEMENT_LABELS: Readonly<Record<AiSettlementStatus, string>> = {
  reserved: 'سهمیهٔ رزرو شده',
  usage_unknown: 'در انتظار تعیین مصرف',
  settled: 'تسویه‌شده',
  released: 'آزادشده',
};

export function executionLabel(execution: AiExecution): string {
  return EXECUTION_LABELS[execution.status];
}

export function settlementLabel(settlement: AiSettlement): string {
  return SETTLEMENT_LABELS[settlement.status];
}

/** Settlement is open while the backend still holds the amount (`reserved`, `usage_unknown`). */
export function isSettlementOpen(settlement: AiSettlement): boolean {
  return settlement.status === 'reserved' || settlement.status === 'usage_unknown';
}

/**
 * Money lines of a settlement, exactly as the backend reports them: the amount
 * still held while it is open, the consumed and released amounts only after
 * settlement or release. Nothing is announced as ended or released unless the
 * backend's status says so.
 */
export function settlementLines(
  settlement: AiSettlement,
): readonly { readonly label: string; readonly value: string }[] {
  if (isSettlementOpen(settlement)) {
    return [{ label: 'سهمیه رزروشده', value: formatMoneyAsTokens(settlement.held) }];
  }
  return [
    { label: 'مصرف‌شده', value: formatMoneyAsTokens(settlement.consumed) },
    { label: 'آزادشده', value: formatMoneyAsTokens(settlement.released) },
  ];
}
